import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import Client from '../components/Client';
import Editor from '../components/Editor';
import { useLocation, useNavigate, Navigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { createPusherClient } from '../pusherClient';

function membersToClients(channel) {
    const list = [];
    if (!channel?.members) {
        return list;
    }
    channel.members.each((member) => {
        list.push({
            socketId: member.id,
            username: member.info?.name || 'Anonymous',
        });
    });
    return list;
}

function EditorPage() {
    const pusherRef = useRef(null);
    const channelRef = useRef(null);
    const codeRef = useRef(null);
    const location = useLocation();
    const { roomId } = useParams();
    const reactNavigator = useNavigate();
    const [ clients, setClients ] = useState([]);
    const [ channelReady, setChannelReady ] = useState(false);

    const userId = useMemo(() => {
        const key = 'collab-editor-user-id';
        try {
            let id = sessionStorage.getItem(key);
            if (!id) {
                id = uuidV4();
                sessionStorage.setItem(key, id);
            }
            return id;
        } catch {
            return uuidV4();
        }
    }, []);

    const onCodeChange = useCallback((code) => {
        codeRef.current = code;
    }, []);

    useEffect(() => {
        const username = location.state?.username;
        if (!username) {
            return undefined;
        }

        let cancelled = false;

        const init = async () => {
            let pusher;
            try {
                pusher = await createPusherClient({ userId, username });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Pusher init failed', e);
                toast.error(
                    'Pusher is not configured. Set PUSHER_KEY, PUSHER_SECRET, PUSHER_CLUSTER, and PUSHER_APP_ID on Vercel (see DEPLOYMENT.md), then redeploy.',
                );
                reactNavigator('/');
                return;
            }

            pusherRef.current = pusher;

            const channelName = `presence-editor-${roomId}`;
            const channel = pusher.subscribe(channelName);
            channelRef.current = channel;

            const onConnectionError = (err) => {
                // eslint-disable-next-line no-console
                console.error('Pusher connection error', err);
                toast.error('Realtime connection failed. Check Pusher credentials and try again.');
                reactNavigator('/');
            };

            pusher.connection.bind('error', onConnectionError);

            channel.bind('pusher:subscription_succeeded', () => {
                if (cancelled) {
                    return;
                }
                setClients(membersToClients(channel));
                setChannelReady(true);
            });

            channel.bind('pusher:member_added', (member) => {
                if (cancelled) {
                    return;
                }
                setClients(membersToClients(channel));
                if (member?.id !== userId) {
                    toast.success(`${member.info?.name || 'Someone'} joined the room.`);
                    channel.trigger('client-code-sync', {
                        code: codeRef.current ?? '',
                        targetUserId: member.id,
                    });
                }
            });

            channel.bind('pusher:member_removed', (member) => {
                if (cancelled) {
                    return;
                }
                setClients(membersToClients(channel));
                if (member?.info?.name) {
                    toast.success(`${member.info.name} left the room`);
                }
            });

            channel.bind('pusher:subscription_error', (status) => {
                // eslint-disable-next-line no-console
                console.error('Pusher subscription error', status);
                toast.error('Failed to join the room channel.');
                reactNavigator('/');
            });
        };

        void init();

        return () => {
            cancelled = true;
            setChannelReady(false);
            const pusher = pusherRef.current;
            const channelName = `presence-editor-${roomId}`;
            if (pusher) {
                try {
                    pusher.unsubscribe(channelName);
                } catch {
                    /* noop */
                }
                pusher.disconnect();
            }
            pusherRef.current = null;
            channelRef.current = null;
        };
    }, [ location.state?.username, reactNavigator, roomId, userId ]);

    async function copyRoomId() {
        try {
            await navigator.clipboard.writeText(roomId);
            toast.success('Room ID copied to clipboard');
        } catch (error) {
            toast.error('Failed to copy room ID');
        }
    }

    function leaveRoom() {
        const channelName = `presence-editor-${roomId}`;
        pusherRef.current?.unsubscribe(channelName);
        pusherRef.current?.disconnect();
        pusherRef.current = null;
        channelRef.current = null;
        reactNavigator('/');
    }

    if (!location.state) {
        return <Navigate to="/" />
    }

    return (
        <div className="mainWrap">
            <div className="aside">
                <div className="asideInner">
                    <div className="logo">
                        <img className="logoImage" src="/logo.png" alt="logo" />
                    </div>

                    <h3>Connected</h3>

                    <div className="clientsList">
                        {
                            clients.map((client) => (
                                <Client key={client.socketId} username={client.username} />
                            ))
                        }
                    </div>
                </div>

                <button type="button" className="btn copyBtn" onClick={copyRoomId} >Copy ROOM ID</button>
                <button type="button" className="btn leaveBtn" onClick={leaveRoom} >Leave</button>
            </div>

            <div className="editorWrap">
                <Editor
                    channelRef={channelRef}
                    channelReady={channelReady}
                    userId={userId}
                    onCodeChange={onCodeChange}
                />
            </div>
        </div>
    )
}

export default EditorPage;
